import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { requestPasswordReset } from '@/features/auth/api'

const schema = z.object({
  email: z.string().min(1, 'Enter your email address').email('That is not a valid email address'),
})

type FormValues = z.infer<typeof schema>

/**
 * Requests a reset link.
 *
 * The confirmation is deliberately the same whether or not the address is registered — the
 * server answers that way on purpose, and showing "no such account" here would turn this
 * form into a way of testing which of a list of emails works here.
 */
export function ForgotPassword() {
  const [sent, setSent] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null)
    try {
      setSent(await requestPasswordReset(email))
    } catch (error) {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Check that the API is running.',
      )
    }
  })

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to set a new one"
      footer={
        <p className="text-center text-sm">
          <Link to="/login" className="text-brand-600 hover:underline dark:text-brand-400">
            Back to sign in
          </Link>
        </p>
      }
    >
      {sent ? (
        <Alert tone="success" title="Check your email">
          {sent} The link is valid for one hour and can be used once.
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="Email" error={errors.email?.message}>
            {(props) => (
              <Input
                {...props}
                {...register('email')}
                type="email"
                autoComplete="username"
                autoFocus
                placeholder="you@company.com"
              />
            )}
          </Field>

          <Button type="submit" className="w-full" loading={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
